import { useMemo, useState } from "react";
import { useTripStore } from "../store/tripStore";
import {
  collectUsedTimezoneIds,
  formatTimezoneLabel,
} from "../utils/timezoneDisplay";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { TimezoneCatalogModal } from "./TimezoneCatalogModal";
import { TripTimezoneSelect } from "./TripTimezoneSelect";
import { TripTravelSettings } from "./TripTravelSettings";

export function TripTimezoneSettingsModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const trip = useTripStore((state) => state.trip);
  const schedule = useTripStore((state) => state.schedule);
  const isReadOnly = useTripStore((state) => state.isReadOnly);
  const setTripTimezones = useTripStore((state) => state.setTripTimezones);
  const [catalogOpen, setCatalogOpen] = useState(false);

  const unlistedUsedZones = useMemo(() => {
    if (!trip) return [];
    const listed = new Set([trip.defaultTimezone, ...trip.timezoneIds]);
    return collectUsedTimezoneIds(trip.defaultTimezone, schedule).filter(
      (zone) => !listed.has(zone),
    );
  }, [schedule, trip]);

  if (!trip) return null;
  const readOnly = isReadOnly();

  const updateDefault = (defaultTimezone: string) => {
    if (defaultTimezone === trip.defaultTimezone) return;
    setTripTimezones(defaultTimezone, [
      trip.defaultTimezone,
      ...trip.timezoneIds.filter((zone) => zone !== defaultTimezone),
    ]);
  };

  const moveZone = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= trip.timezoneIds.length) return;
    const next = [...trip.timezoneIds];
    [next[index], next[target]] = [next[target], next[index]];
    setTripTimezones(trip.defaultTimezone, next);
  };

  const addZone = (timezoneId: string) => {
    if (timezoneId !== trip.defaultTimezone) {
      setTripTimezones(trip.defaultTimezone, [...trip.timezoneIds, timezoneId]);
    }
    setCatalogOpen(false);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title="Trip settings"
        description="Timezones used by calendar controls, and how travel between stops is estimated."
        icon="tune"
        size="lg"
        footer={
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        }
      >
        <div className="flex flex-col gap-5">
          <section aria-labelledby="default-timezone-heading">
            <div className="mb-2 flex items-center justify-between gap-3">
              <h4
                id="default-timezone-heading"
                className="m-0 text-xs font-extrabold uppercase tracking-wider text-secondary"
              >
                Default timezone
              </h4>
              <span className="rounded-md bg-sky-50 px-2 py-1 text-2xs font-extrabold uppercase tracking-wide text-blue-700">
                Always listed
              </span>
            </div>
            <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-4">
              <TripTimezoneSelect
                value={trip.defaultTimezone}
                date={trip.startDate}
                onChange={updateDefault}
                disabled={readOnly}
                data-testid="trip-default-timezone-select"
              />
              <p className="mb-0 mt-2 text-xs font-medium text-blue-800">
                Changing the display default preserves event instants. Matching
                day overrides are removed as redundant.
              </p>
            </div>
          </section>

          <section aria-labelledby="trip-timezone-list-heading">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div>
                <h4
                  id="trip-timezone-list-heading"
                  className="m-0 text-xs font-extrabold uppercase tracking-wider text-secondary"
                >
                  Trip shortlist
                </h4>
                <p className="mb-0 mt-1 text-xs font-medium text-tertiary">
                  Order controls how zones appear in calendar fields.
                </p>
              </div>
              {!readOnly && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon="add"
                  onClick={() => setCatalogOpen(true)}
                >
                  Add timezone
                </Button>
              )}
            </div>

            <ol
              className="m-0 list-none space-y-2 p-0"
              data-testid="trip-timezone-list"
            >
              {trip.timezoneIds.map((zone, index) => (
                <li
                  key={zone}
                  className="flex items-center gap-3 rounded-xl border border-border bg-surface px-3 py-3 shadow-sm"
                >
                  <span
                    className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-subtle text-xs font-extrabold text-secondary"
                    aria-hidden
                  >
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold text-primary">
                      {formatTimezoneLabel(zone, trip.startDate)}
                    </span>
                    <span className="block truncate text-xs font-medium text-tertiary">
                      {zone}
                    </span>
                  </span>
                  {!readOnly && (
                    <span className="flex shrink-0 items-center gap-0.5">
                      <Button
                        variant="unstyled"
                        onClick={() => moveZone(index, -1)}
                        disabled={index === 0}
                        className="rounded-md p-1.5 text-tertiary hover:bg-subtle hover:text-primary disabled:opacity-30"
                        aria-label={`Move ${zone} up`}
                      >
                        <span
                          className="material-symbols-outlined text-lg"
                          aria-hidden
                        >
                          arrow_upward
                        </span>
                      </Button>
                      <Button
                        variant="unstyled"
                        onClick={() => moveZone(index, 1)}
                        disabled={index === trip.timezoneIds.length - 1}
                        className="rounded-md p-1.5 text-tertiary hover:bg-subtle hover:text-primary disabled:opacity-30"
                        aria-label={`Move ${zone} down`}
                      >
                        <span
                          className="material-symbols-outlined text-lg"
                          aria-hidden
                        >
                          arrow_downward
                        </span>
                      </Button>
                      <Button
                        variant="unstyled"
                        onClick={() =>
                          setTripTimezones(
                            trip.defaultTimezone,
                            trip.timezoneIds.filter((item) => item !== zone),
                          )
                        }
                        className="rounded-md p-1.5 text-tertiary hover:bg-error-bg hover:text-error"
                        aria-label={`Remove ${zone} from trip`}
                      >
                        <span
                          className="material-symbols-outlined text-lg"
                          aria-hidden
                        >
                          close
                        </span>
                      </Button>
                    </span>
                  )}
                </li>
              ))}
              {trip.timezoneIds.length === 0 && (
                <li className="rounded-xl border border-dashed border-border px-4 py-5 text-center text-sm font-semibold text-secondary">
                  No optional timezones yet.
                </li>
              )}
            </ol>
          </section>

          {unlistedUsedZones.length > 0 && (
            <section
              aria-labelledby="used-timezones-heading"
              className="rounded-xl border border-amber-200 bg-amber-50 p-4"
            >
              <h4
                id="used-timezones-heading"
                className="m-0 text-xs font-extrabold uppercase tracking-wider text-amber-800"
              >
                Used but not listed
              </h4>
              <p className="mb-3 mt-1 text-xs font-medium text-amber-800">
                These event or day zones remain valid even if they are not in
                the shortlist.
              </p>
              <div className="flex flex-col gap-2">
                {unlistedUsedZones.map((zone) => (
                  <div
                    key={zone}
                    className="flex items-center justify-between gap-3 rounded-lg bg-white/80 px-3 py-2"
                  >
                    <span className="min-w-0 truncate text-sm font-bold text-amber-950">
                      {formatTimezoneLabel(zone, trip.startDate)}
                    </span>
                    {!readOnly && (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon="add"
                        onClick={() => addZone(zone)}
                      >
                        Add to trip
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          <TripTravelSettings readOnly={readOnly} />

          {readOnly && (
            <p className="m-0 rounded-xl bg-subtle px-4 py-3 text-sm font-semibold text-secondary">
              You have read-only access to these trip settings.
            </p>
          )}
        </div>
      </Modal>
      <TimezoneCatalogModal
        open={catalogOpen}
        date={trip.startDate}
        onClose={() => setCatalogOpen(false)}
        onSelect={addZone}
      />
    </>
  );
}
